import {
  computeSpansByCoordinates,
  computeCellStyles,
  getTemplateList,
} from '../utils';
import type {LayoutMatrix, LayoutStructure, LayoutStyles} from '../types';

/**
 * Parses the matrix and computes the layout structure so that
 * for each Cell we know its position and its col and row span styles.
 *
 * * Example: 2 by 2 Layout with 3 Cells (cell 3 has been merged to the top)
 *
 * Matrix:
 * [
 *   ['item_id_3', 'item_id_2'],
 *   ['item_id_3', 'item_id_4'],
 * ]
 *
 * Structure:
 * {
 *   { item_id_3: { position: 0, styles: { gridRowStart: `span 2`, gridRowEnd: `span 2`, gridColumnStart: null, gridColumnEnd: null } },
 *   { item_id_2: { position: 1, styles: { gridRowStart: null, gridRowEnd: null, gridColumnStart: null, gridColumnEnd: null } },
 *   { item_id_4: { position: 2, styles: { gridRowStart: null, gridRowEnd: null, gridColumnStart: null, gridColumnEnd: null } },
 * }
 */
const computeLayoutStructure = (
  matrix: LayoutMatrix,
  layoutStyles?: LayoutStyles
): LayoutStructure => {
  const output: Record<string, any> = {};
  let position = 0;
  for (let row = 0; row < matrix.length; row++) {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    for (let col = 0; col < matrix[row].length; col++) {
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      const cellId = matrix[row][col];
      if (!cellId) {
        continue;
      }

      const coords = [row, col];
      if (!output[cellId]) {
        output[cellId] = {
          position: position++,
          coordinates: [coords],
        };
      } else {
        output[cellId].coordinates.push(coords);
      }
    }
  }

  const maxColumnSpan = layoutStyles?.gridTemplateColumns
    ? getTemplateList(layoutStyles.gridTemplateColumns).length
    : null;

  return Object.entries(output).reduce<Record<string, any>>(
    (results, [cellId, data]: [any, any]) => {
      const spans = computeSpansByCoordinates(data.coordinates);

      // We want to cap the Cell's column span to the maximum
      // number of columns in the Layout
      if (maxColumnSpan && spans.colSpan > maxColumnSpan) {
        spans.colSpan = maxColumnSpan;
      }

      return {
        ...results,
        [cellId]: {
          position: data.position,
          styles: computeCellStyles({
            spans,
          }),
        },
      };
    },
    {}
  );
};

export default computeLayoutStructure;
